import { format } from 'date-fns';
import type { TFunction } from 'i18next';
import type { NameOfFrequency } from 'vopay';
import type { WeekdayCode } from '../WeekdayToggleGroup/WeekdayToggleGroup';
import type { CustomRecurrenceUnit } from '../CustomRecurrenceFields/CustomRecurrenceFields';

const BY_DAY_WEEKDAY_CODES: WeekdayCode[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

export function isWeekdayCode(code: string): code is WeekdayCode {
  return (BY_DAY_WEEKDAY_CODES as string[]).includes(code);
}

export const CUSTOM_UNIT_TO_NAME_OF_FREQUENCY: Record<CustomRecurrenceUnit, NameOfFrequency> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
  year: 'annually',
};
export const NAME_OF_FREQUENCY_TO_CUSTOM_UNIT: Partial<Record<NameOfFrequency, CustomRecurrenceUnit>> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  annually: 'year',
};

// Maps an ordinal code to the i18n key segment / English defaultValue for it.
export const ORDINAL_CODE_TO_WORD: Record<string, string> = {
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '-1': 'last',
};

// Translates an ordinal code ('1'..'4', '-1') via the UI.RecurrencePresetSelect namespace —
// pass the `t` from that keyPrefix (RecurrencePresetSelect, useTransactionDetailsFields, and
// buildElinxRows.helper.tsx all already bind one there to keep this wording in sync).
export function ordinalLabel(t: TFunction, code: string): string {
  const word = ORDINAL_CODE_TO_WORD[code] || '';
  return t(`Ordinal.${word}`, { defaultValue: word });
}

// Which occurrence of its weekday this date is within its month (e.g. 3rd Thursday), as the
// API's ordinal code ('1'..'4', or '-1' for the month's last occurrence — a 5th occurrence is
// always the last one, since monthly ByDay ordinals only go up to 4 plus "last").
export function getOrdinalWeekdayCode(date: Date): string {
  const occurrence = Math.ceil(date.getDate() / 7);
  return occurrence >= 5 ? '-1' : String(occurrence);
}

// Same ordinal, formatted as the API's ByDay code (e.g. "4MO", "-1FR").
export function getOrdinalWeekdayByDayCode(date: Date): string {
  return `${getOrdinalWeekdayCode(date)}${BY_DAY_WEEKDAY_CODES[date.getDay()]}`;
}

// The date's own weekday as a plain ByDay code (e.g. "TH") — used to anchor the "Weekly on
// {weekday}" quick preset to the selected date's weekday, per the API's FR-1 mapping.
export function getWeekdayByDayCode(date: Date): WeekdayCode {
  return BY_DAY_WEEKDAY_CODES[date.getDay()];
}

// Formats a millisecond-timestamp form value as 'yyyy-MM-dd' for the API, returning '' for any
// missing/invalid value instead of throwing — a DatepickerSimple field can hold '' transiently
// (e.g. right after mount, before the user has picked a date).
export function formatTimestamp(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(Number(value));
  return Number.isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd');
}
